import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'

const FactoryContext = createContext(null)

/**
 * Provider para la fábrica seleccionada.
 * Persiste la selección en el query param `?factory=` de la URL:
 *  - Al cargar, lee ?factory= y lo usa como valor inicial.
 *  - Al cambiar, actualiza la URL con replace (no genera nueva entrada en historial).
 *  - Al cargar las fábricas del backend, valida que el valor de la URL sea válido;
 *    si no lo es, lo elimina y deja que App.jsx aplique el fallback habitual.
 */
export function FactoryProvider({ children }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/'
  const isPublicBootPath = pathname === '/login' || pathname.startsWith('/mobile/')

  // Leer fábrica inicial desde URL (solo en el primer render)
  const [selectedFactory, setSelectedFactoryState] = useState(() => {
    const param = searchParams.get('factory')
    return param ? param.toUpperCase() : null
  })

  const [factories, setFactories] = useState([])
  const [loadingFactories, setLoadingFactories] = useState(false)
  const [factoriesError, setFactoriesError] = useState(null)

  // Cargar fábricas desde el backend (mr_factories)
  useEffect(() => {
    if (isPublicBootPath) {
      setLoadingFactories(false)
      return undefined
    }

    let cancelled = false

    const loadFactories = async () => {
      console.log('[FactoryContext] Iniciando carga de fábricas desde /auth/factories')
      setLoadingFactories(true)
      setFactoriesError(null)

      try {
        let data = await api.get('/api/auth/factories')
        if (data=='') {
          data = await api.get('/api/asset/list')
          data = data.filter((item) => item.ParentID === null) // Solo fábricas raíz'
        }
         
        if (cancelled) {
          console.log('[FactoryContext] Respuesta recibida pero efecto cancelado, ignorando')
          return
        }

        console.log('[FactoryContext] Respuesta cruda de /auth/factories:', data)

        const list = Array.isArray(data) ? data : (data?.data || [])
        console.log('[FactoryContext] Lista normalizada de fábricas:', list)

        setFactories(list)
      } catch (error) {
        console.error('[FactoryContext] Error cargando fábricas desde /auth/factories:', error)
        if (!cancelled) {
          setFactoriesError(error)
        }
      } finally {
        if (!cancelled) {
          setLoadingFactories(false)
          console.log('[FactoryContext] Finalizada carga de fábricas')
        }
      }
    }

    loadFactories()

    return () => {
      console.log('[FactoryContext] Cleanup efecto de carga de fábricas (cancelled = true)')
      cancelled = true
    }
  }, [isPublicBootPath])

  // Códigos de fábrica derivados de los datos completos
  const factoryCodes = useMemo(() => {
    const codes = factories
      .map((f) => (f.asset_code || f.assetCode || f.Code || f.code || f.name || '').toUpperCase())
      .filter(Boolean)

    console.log('[FactoryContext] factoryCodes derivados:', codes)
    return codes
  }, [factories])

  // Una vez cargadas las fábricas, validar que la selección actual (venida de URL) sea válida.
  // Si no existe en la lista del usuario, limpiarla para que App.jsx aplique el fallback.
  useEffect(() => {
    if (factoryCodes.length === 0) return

    if (selectedFactory && !factoryCodes.includes(selectedFactory)) {
      console.warn(
        `[FactoryContext] La fábrica "${selectedFactory}" de la URL no está en la lista del usuario. Usando fallback.`
      )
      setSelectedFactoryState(null)
      setSearchParams(
        (prev) => { const next = new URLSearchParams(prev); next.delete('factory'); return next },
        { replace: true }
      )
    }
  }, [factoryCodes])

  // Setter público: actualiza estado + URL en un solo paso
  const setSelectedFactory = useCallback((factory) => {
    const normalized = factory ? String(factory).toUpperCase() : null
    setSelectedFactoryState(normalized)
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (normalized) {
          next.set('factory', normalized)
        } else {
          next.delete('factory')
        }
        return next
      },
      { replace: true }
    )
  }, [setSearchParams])

  const value = {
    selectedFactory,
    setSelectedFactory,
    factories,
    factoryCodes,
    loadingFactories,
    factoriesError,
  }
  return (
    <FactoryContext.Provider value={value}>
      {children}
    </FactoryContext.Provider>
  )
}

/**
 * Hook para consumir la fábrica seleccionada y su setter.
 * Debe usarse dentro de FactoryProvider.
 */
export function useFactory() {
  const context = useContext(FactoryContext)
  if (context == null) {
    throw new Error('useFactory must be used within a FactoryProvider')
  }
  return context
}
